import { useMemo } from 'react';
import { useMutation } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { api } from '@/api';
import { PATHS } from '@/common';
import { RAW_DATE_FORMAT } from '@/common/constants';
import { useAuthStore } from '@/modules/user';
import { formatDependentSsnToServer } from '@/utils';

const CREATION_SOURCE_TYPE = 'WEB_PARTICIPANT_PORTAL';

interface BasePersonDtoPayload {
  id: string;
  preferred_name: string;
  first_name: string;
  last_name: string;
}
export interface DependentInfoDtoPayload {
  id?: string;
  first_name: string;
  last_name: string;
  ssn?: string;
  local_date_birth_on?: string;
  birth_on?: string;
  employee_id?: string;
  person: BasePersonDtoPayload;
  relationship_type: string;
  creation_source_type?: string;
}

interface BasePersonDto {
  id: string;
  preferredName: string;
  firstName: string;
  lastName: string;
}
export interface DependentInfoDto {
  id?: string;
  firstName: string;
  lastName: string;
  ssn?: string;
  birthOn: string;
  person: BasePersonDto;
  relationshipType: string;
}

const preparedDependentInfo = (data: DependentInfoDto, employeeId?: string): DependentInfoDtoPayload => ({
  id: data.id,
  first_name: data.firstName,
  last_name: data.lastName,
  ...formatDependentSsnToServer(data.ssn),
  birth_on: dayjs(data.birthOn).format(RAW_DATE_FORMAT),
  person: {
    id: data.person.id,
    preferred_name: data.person.preferredName,
    first_name: data.person.firstName,
    last_name: data.person.lastName,
  },
  employee_id: employeeId,
  relationship_type: data.relationshipType,
  creation_source_type: CREATION_SOURCE_TYPE,
});

const CREATE_DEPENDENT_KEY = 'update_card_holder';

export const useAddCardDependentMutation = (onSuccess?: (dependent: DependentInfoDto) => void) => {
  const { user } = useAuthStore();
  const query = useMemo(() => {
    const queryGenerator = new URLSearchParams();

    queryGenerator.set(CREATE_DEPENDENT_KEY, 'true');

    return queryGenerator;
  }, []);

  const { mutateAsync, isLoading, isError } = useMutation(
    (data: DependentInfoDto) => api.post(`${PATHS.CREATE_DEPENDENT}?${query}`, preparedDependentInfo(data, user?.employee?.id)),
    {
      onSuccess: (_, value) => {
        if (onSuccess) onSuccess(value);
      },
    }
  );

  return {
    mutateAsync,
    isLoading,
    isError,
  };
};

export const useUpdateCardDependentMutation = () => {
  const { mutateAsync, isLoading, isError } = useMutation((data: DependentInfoDto) =>
    api.post(PATHS.ADD_CARD_FOR_DEPENDENT(data.id), preparedDependentInfo(data))
  );

  return {
    mutateAsync,
    isLoading,
    isError,
  };
};
